import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Drawer, useToast } from "@/components/system";
import {
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
} from "@/components/forms";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import {
  ACTION_CATEGORIES,
  type ActionCategory,
} from "@/hooks/use-action-items";
import { formatError } from "@/lib/format-error";

/**
 * HOTFIX-BW19.4 PR-A Bug 6: manual task creation.
 *
 * Until BW19 PR-E shipped, every action_item entered the system through
 * capture triage. This dialog adds a direct-entry path so a quick TODO
 * doesn't have to round-trip through the inbox. Inserts a row into the
 * existing action_item table; /tasks already lists everything from
 * action_item so the new row appears the moment the mutation succeeds.
 */

type ProjectChoice = { id: string; name: string };

const DEFAULT_CATEGORY: ActionCategory = "admin";

export function NewTaskDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ActionCategory>(DEFAULT_CATEGORY);
  const [projectId, setProjectId] = useState<string>("");
  const [dueAt, setDueAt] = useState<string>("");
  const [effortMin, setEffortMin] = useState<string>("");

  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setCategory(DEFAULT_CATEGORY);
      setProjectId("");
      setDueAt("");
      setEffortMin("");
    }
  }, [open]);

  const projectsQuery = useQuery<ProjectChoice[]>({
    queryKey: ["task_dialog_projects", userId],
    enabled: Boolean(userId && open),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("project")
        .select("id, name")
        .eq("user_id", userId)
        .order("name");
      if (error) throw error;
      return (data ?? []) as ProjectChoice[];
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const trimmed = title.trim();
      if (!trimmed) throw new Error("Title is required");
      const effort = effortMin ? Number(effortMin) : null;
      const effortVal =
        effort != null && Number.isFinite(effort) && effort > 0
          ? Math.round(effort)
          : null;
      const { error } = await supabase.from("action_item").insert({
        user_id: userId,
        title: trimmed,
        description: description.trim() || null,
        category,
        state: "open",
        project_id: projectId || null,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        effort_estimate_min: effortVal,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["action-items"] });
      void qc.invalidateQueries({ queryKey: ["today_tasks"] });
      toast.show("Task added.");
      onClose();
    },
    onError: (err) => toast.show(`Could not add task, ${formatError(err)}`),
  });

  function onKeyDown(e: React.KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (title.trim()) create.mutate();
    }
  }

  const projects = projectsQuery.data ?? [];

  return (
    <Drawer open={open} onClose={onClose} title="New task" size="md">
      <div
        onKeyDown={onKeyDown}
        style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}
      >
        <label style={fieldStyle()}>
          <span style={labelStyle()}>Title</span>
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Renew passport"
            style={inputStyle()}
          />
        </label>

        <label style={fieldStyle()}>
          <span style={labelStyle()}>Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional context, links, blockers."
            rows={3}
            style={{ ...inputStyle(), resize: "vertical", minHeight: 70 }}
          />
        </label>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "var(--space-3)",
          }}
        >
          <label style={fieldStyle()}>
            <span style={labelStyle()}>Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ActionCategory)}
              style={inputStyle()}
            >
              {ACTION_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldStyle()}>
            <span style={labelStyle()}>Project</span>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              style={inputStyle()}
            >
              <option value="">No project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "var(--space-3)",
          }}
        >
          <label style={fieldStyle()}>
            <span style={labelStyle()}>Due</span>
            <input
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              style={inputStyle()}
            />
          </label>

          <label style={fieldStyle()}>
            <span style={labelStyle()}>Effort (min)</span>
            <input
              type="number"
              min={1}
              step={5}
              value={effortMin}
              onChange={(e) => setEffortMin(e.target.value)}
              placeholder="30"
              style={inputStyle()}
            />
          </label>
        </div>

        <DialogFooter>
          <SecondaryButton onClick={onClose} disabled={create.isPending}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={() => create.mutate()}
            disabled={create.isPending || title.trim().length === 0}
          >
            {create.isPending ? "Adding..." : "Add task"}
          </PrimaryButton>
        </DialogFooter>
      </div>
    </Drawer>
  );
}

function fieldStyle(): React.CSSProperties {
  return { display: "grid", gap: 4, minWidth: 0 };
}

function labelStyle(): React.CSSProperties {
  return {
    fontSize: "var(--type-meta)",
    color: "var(--fg-3)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  };
}

function inputStyle(): React.CSSProperties {
  return {
    height: 36,
    padding: "0 var(--space-3)",
    background: "var(--bg-3)",
    color: "var(--fg)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-card)",
    fontFamily: "var(--font-sans)",
    fontSize: "var(--type-body)",
  };
}
